import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import styled from "styled-components";
import { COLOR, MEDIA_QUERY } from "@xe/constants";
import { toRgba, lightenOrDarkenColor } from "@xe/util-color";
import { ButtonCapsule, ButtonLarge, ButtonSmall, ButtonSmallOld, OldButton } from "./buttons-to-deprecate";
export { ButtonCapsule, ButtonLarge, ButtonSmall, ButtonSmallOld, OldButton };
var sizeMap = {
  sm: {
    fontSize: 1.4,
    padding: 6
  },
  md: {
    fontSize: 1.6,
    padding: 10
  },
  lg: {
    fontSize: 2,
    padding: 14
  }
};
var BaseButton =
/*#__PURE__*/
styled("button").withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !["colorScheme", "hasIconOnly", "isHollow", "isStretched", "isFullyStretchedOnExtraSmallDevices", "size"].includes(prop);
  }
}).withConfig({
  displayName: "BaseButton"
})(["display:inline-flex;justify-content:center;align-items:center;min-width:", ";width:", ";", "{width:auto;}cursor:pointer;margin:0;outline-offset:4px;font-weight:bold;text-align:center;text-decoration:none;transition-property:background-color,border-color;transition-duration:0.3s;", " ", " ", ""], function (_ref) {
  var isStretched = _ref.isStretched;
  return isStretched ? "220px" : 0;
}, function (_ref2) {
  var isFullyStretchedOnExtraSmallDevices = _ref2.isFullyStretchedOnExtraSmallDevices;
  return isFullyStretchedOnExtraSmallDevices ? "100%" : "auto";
}, MEDIA_QUERY.minWidthXs, function (_ref3) {
  var disabled = _ref3.disabled;
  return disabled ? "\n    opacity: 0.38;\n    pointer-events: none;\n  " : "";
}, function (_ref4) {
  var size = _ref4.size,
      hasIconOnly = _ref4.hasIconOnly;
  var _sizeMap$size = sizeMap[size],
      fontSize = _sizeMap$size.fontSize,
      padding = _sizeMap$size.padding;
  return "\n      padding: " + padding + "px " + (hasIconOnly ? padding : padding * 2) + "px;\n      font-size: " + fontSize + "rem;\n      & .button-icon {\n        width: " + fontSize + "rem;\n        height: " + fontSize + "rem;\n        color: currentColor;\n        flex-shrink: 0;\n        " + (!hasIconOnly ? "margin-right: 8px;" : "") + "\n      }\n    ";
}, function (_ref5) {
  var theme = _ref5.theme,
      colorScheme = _ref5.colorScheme,
      isHollow = _ref5.isHollow;
  var mainColor = theme.button[colorScheme][isHollow ? "hollow" : "solid"];
  var textColor = isHollow ? mainColor : "#fff";
  return "\n      color: " + textColor + ";\n      &:link,\n      &:active,\n      &:visited {\n        /* overrides the value of a:link, a:active, and a:visited in charts.css in xecom */\n        color: " + textColor + ";\n      }\n      background: " + (isHollow ? "none" : mainColor) + ";\n      border: 2px solid " + mainColor + ";\n      border-radius: 8px;\n\n      &:hover {\n        box-shadow: 0 2px 4px 0 " + toRgba(COLOR.grey1, 0.7) + ";\n        background: " + (isHollow ? toRgba(mainColor, 0.15) : lightenOrDarkenColor(mainColor, 15)) + ";\n        " + (!isHollow ? "border-color: " + lightenOrDarkenColor(mainColor, 15) + ";" : "") + "\n      }\n      &:active {\n        background: " + (isHollow ? toRgba(mainColor, 0.2) : lightenOrDarkenColor(mainColor, -20)) + ";\n        " + (!isHollow ? "border-color: " + lightenOrDarkenColor(mainColor, -20) + ";" : "") + "\n        box-shadow: 0 0 0 4px " + toRgba(mainColor, 0.5) + ";\n      }\n    ";
});
var Button =
/*#__PURE__*/
forwardRef(function Button(_ref6, ref) {
  var children = _ref6.children,
      _ref6$colorScheme = _ref6.colorScheme,
      colorScheme = _ref6$colorScheme === void 0 ? "primary" : _ref6$colorScheme,
      _ref6$isHollow = _ref6.isHollow,
      isHollow = _ref6$isHollow === void 0 ? false : _ref6$isHollow,
      _ref6$disabled = _ref6.disabled,
      disabled = _ref6$disabled === void 0 ? false : _ref6$disabled,
      _ref6$size = _ref6.size,
      size = _ref6$size === void 0 ? "md" : _ref6$size,
      _ref6$isStretched = _ref6.isStretched,
      isStretched = _ref6$isStretched === void 0 ? false : _ref6$isStretched,
      _ref6$isFullyStretche = _ref6.isFullyStretchedOnExtraSmallDevices,
      isFullyStretchedOnExtraSmallDevices = _ref6$isFullyStretche === void 0 ? false : _ref6$isFullyStretche,
      Icon = _ref6.Icon,
      rest = _objectWithoutPropertiesLoose(_ref6, ["children", "colorScheme", "isHollow", "disabled", "size", "isStretched", "isFullyStretchedOnExtraSmallDevices", "Icon"]);

  return (
    /*#__PURE__*/
    React.createElement(BaseButton, _extends({
      ref: ref,
      colorScheme: colorScheme,
      isHollow: isHollow,
      disabled: disabled,
      size: size,
      isStretched: isStretched,
      isFullyStretchedOnExtraSmallDevices: isFullyStretchedOnExtraSmallDevices,
      hasIconOnly: Icon && !children
    }, rest), Icon &&
    /*#__PURE__*/
    React.createElement(Icon, {
      className: "button-icon"
    }), children)
  );
});
Button.propTypes = {
  children: PropTypes.node,
  colorScheme: PropTypes.oneOf(["primary", "danger"]),
  isHollow: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  isStretched: PropTypes.bool,
  isFullyStretchedOnExtraSmallDevices: PropTypes.bool,
  Icon: PropTypes.elementType
};
export default Button;